/**
 * Premium Analyzer — shows whether option premiums are cheap or expensive.
 *
 * For each strike (ATM +/- 5):
 *   - LTP, IV, Intrinsic Value, Time Value, Time Value/Day
 *   - Tag: CHEAP / FAIR / EXPENSIVE (based on IV vs 20-day HV)
 *   - Suggestion banner with directional guidance
 */

import React, { useState, useCallback } from "react";
import axios from "axios";

const API = "/api/zerodha";

const TAG_COLORS = {
  CHEAP: { bg: "var(--tint-pos-strong)", color: "#34d399", border: "#059669" },
  FAIR: { bg: "var(--tint-warn)", color: "#fbbf24", border: "#d97706" },
  EXPENSIVE: { bg: "var(--tint-neg)", color: "#f87171", border: "#dc2626" },
};

const SUGGESTION_COLORS = {
  HIGH_IV: { bg: "var(--tint-neg)", border: "#dc2626", color: "var(--pale-neg)" },
  LOW_IV: { bg: "var(--tint-pos-strong)", border: "#059669", color: "var(--pale-pos)" },
  CE_EXPENSIVE: { bg: "#451a03", border: "#d97706", color: "#fcd34d" },
  PE_EXPENSIVE: { bg: "#451a03", border: "#d97706", color: "#fcd34d" },
  BALANCED: { bg: "var(--tint-indigo)", border: "#6366f1", color: "var(--pale-indigo)" },
};

function Tag({ tag }) {
  if (!tag) return <span style={{ color: "var(--edge)" }}>-</span>;
  const c = TAG_COLORS[tag] || TAG_COLORS.FAIR;
  return (
    <span style={{
      display: "inline-block", padding: "2px 8px", borderRadius: 4, fontSize: 10,
      fontWeight: 700, background: c.bg, color: c.color, border: `1px solid ${c.border}`,
    }}>{tag}</span>
  );
}

export default function PremiumAnalyzer() {
  const [index, setIndex] = useState("NIFTY");
  const [expiry, setExpiry] = useState("");
  const [expiries, setExpiries] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const fetchAnalysis = useCallback(async (idx, exp) => {
    setLoading(true); setError(""); setData(null);
    try {
      const params = { index: idx };
      if (exp) params.expiry = exp;
      const res = await axios.get(`${API}/premium-analysis`, { params, timeout: 30000 });
      if (res.data.success) {
        setData(res.data.data);
        if (res.data.data.expiries?.length) {
          setExpiries(res.data.data.expiries);
          if (!exp) setExpiry(res.data.data.expiry);
        }
      }
      else setError(res.data.message || "Unknown error");
    } catch (err) {
      setError(err.response?.data?.message || err.message);
    }
    setLoading(false);
  }, []);

  const handleIndexChange = (idx) => {
    setIndex(idx);
    setExpiry("");
    setExpiries([]);
    fetchAnalysis(idx, "");
  };

  const handleExpiryChange = (exp) => {
    setExpiry(exp);
    fetchAnalysis(index, exp);
  };

  const s = data?.suggestion;
  const sc = s ? SUGGESTION_COLORS[s.type] || SUGGESTION_COLORS.BALANCED : null;

  return (
    <div className="five-ema">
      {/* Header */}
      <div className="dashboard-header">
        <div>
          <h2>Premium Analyzer</h2>
          <p className="dashboard-subtitle">IV vs Historical Volatility — are premiums cheap or expensive?</p>
        </div>
        <div className="dashboard-actions">
          {["NIFTY", "BANKNIFTY", "SENSEX"].map(idx => (
            <button key={idx} className={`btn btn-sm ${index === idx ? "btn-primary" : ""}`}
              onClick={() => handleIndexChange(idx)} disabled={loading}>
              {idx}
            </button>
          ))}
          {expiries.length > 0 && (
            <select className="ema-select" value={expiry} onChange={e => handleExpiryChange(e.target.value)} disabled={loading}>
              {expiries.map(exp => (
                <option key={exp} value={exp}>{exp}{exp === expiries[0] ? " (Current)" : ""}</option>
              ))}
            </select>
          )}
          <button className="btn btn-secondary" onClick={() => fetchAnalysis(index, expiry)} disabled={loading}>
            {loading ? "Analyzing..." : "Refresh"}
          </button>
        </div>
      </div>

      {error && <div className="error-msg">{error}</div>}
      {loading && <div className="loading"><div className="spinner" /><p>Fetching option chain & computing IV...</p></div>}

      {data && !loading && (
        <>
          {/* Info Bar */}
          <div style={{ display: "flex", gap: 16, padding: "8px 12px", background: "var(--panel3)", borderBottom: "1px solid var(--surface3)", fontSize: 12, color: "var(--grey-text2)", flexWrap: "wrap", alignItems: "center" }}>
            <span>Spot: <strong style={{ color: "#fff" }}>{data.spot?.toFixed(1)}</strong></span>
            <span>ATM: <strong style={{ color: "#fff" }}>{data.atmStrike}</strong></span>
            <span>Expiry: <strong>{data.expiry}</strong></span>
            <span>Days to Expiry: <strong style={{ color: data.daysToExpiry <= 2 ? "#f87171" : data.daysToExpiry <= 5 ? "#fbbf24" : "#34d399" }}>{data.daysToExpiry}</strong></span>
            <span style={{ color: "var(--edge)" }}>|</span>
            <span>HV(20d): <strong style={{ color: "#8b5cf6" }}>{data.hv20 ? `${data.hv20}%` : "N/A"}</strong></span>
            <span>Avg CE IV: <strong style={{ color: "#60a5fa" }}>{data.avgCeIV ? `${data.avgCeIV}%` : "-"}</strong></span>
            <span>Avg PE IV: <strong style={{ color: "#fb923c" }}>{data.avgPeIV ? `${data.avgPeIV}%` : "-"}</strong></span>
            <span style={{ color: "var(--edge)" }}>|</span>
            <span style={{ color: "var(--btn-border)", fontSize: 10 }}>As of {data.asOf}</span>
          </div>

          {/* Suggestion Banner */}
          {s && (
            <div style={{
              padding: "10px 16px", margin: "8px 12px", borderRadius: 6,
              background: sc.bg, border: `1px solid ${sc.border}`, color: sc.color,
              fontSize: 13, fontWeight: 500,
            }}>
              {s.text}
            </div>
          )}

          {/* IV vs HV visual bar */}
          {data.hv20 && (data.avgCeIV || data.avgPeIV) && (
            <div style={{ padding: "8px 12px", display: "flex", gap: 16, fontSize: 12, flexWrap: "wrap" }}>
              {data.avgCeIV && (
                <div style={{ flex: 1, minWidth: 200 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4, color: "var(--grey-text)" }}>
                    <span>CE IV: {data.avgCeIV}%</span>
                    <span>HV: {data.hv20}%</span>
                    <span>Ratio: {(data.avgCeIV / data.hv20).toFixed(2)}x</span>
                  </div>
                  <div style={{ height: 8, borderRadius: 4, background: "var(--panel3)", overflow: "hidden", position: "relative" }}>
                    <div style={{ position: "absolute", height: "100%", width: `${Math.min(100, (data.hv20 / Math.max(data.avgCeIV, data.hv20)) * 100)}%`, background: "#8b5cf6", borderRadius: 4, opacity: 0.5 }} />
                    <div style={{ position: "absolute", height: "100%", width: `${Math.min(100, (data.avgCeIV / Math.max(data.avgCeIV, data.hv20)) * 100)}%`, background: "#60a5fa", borderRadius: 4, opacity: 0.7 }} />
                  </div>
                </div>
              )}
              {data.avgPeIV && (
                <div style={{ flex: 1, minWidth: 200 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4, color: "var(--grey-text)" }}>
                    <span>PE IV: {data.avgPeIV}%</span>
                    <span>HV: {data.hv20}%</span>
                    <span>Ratio: {(data.avgPeIV / data.hv20).toFixed(2)}x</span>
                  </div>
                  <div style={{ height: 8, borderRadius: 4, background: "var(--panel3)", overflow: "hidden", position: "relative" }}>
                    <div style={{ position: "absolute", height: "100%", width: `${Math.min(100, (data.hv20 / Math.max(data.avgPeIV, data.hv20)) * 100)}%`, background: "#8b5cf6", borderRadius: 4, opacity: 0.5 }} />
                    <div style={{ position: "absolute", height: "100%", width: `${Math.min(100, (data.avgPeIV / Math.max(data.avgPeIV, data.hv20)) * 100)}%`, background: "#fb923c", borderRadius: 4, opacity: 0.7 }} />
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Strikes Table */}
          <div style={{ padding: "8px 12px" }}>
            <div className="stocks-table-wrapper">
              <table className="stocks-table" style={{ fontSize: 12 }}>
                <thead>
                  <tr>
                    <th colSpan={7} style={{ textAlign: "center", background: "#1a2744", color: "#60a5fa", borderRight: "2px solid var(--surface3)" }}>CALL (CE)</th>
                    <th style={{ background: "var(--panel3)", color: "#fff", textAlign: "center" }}>Strike</th>
                    <th colSpan={7} style={{ textAlign: "center", background: "#2d1f1f", color: "#fb923c" }}>PUT (PE)</th>
                  </tr>
                  <tr>
                    <th style={{ color: "#60a5fa" }}>Tag</th>
                    <th style={{ color: "#60a5fa" }}>IV%</th>
                    <th style={{ color: "#60a5fa" }}>LTP</th>
                    <th style={{ color: "#60a5fa" }}>Intrinsic</th>
                    <th style={{ color: "#60a5fa" }}>Time Val</th>
                    <th style={{ color: "#60a5fa" }}>TV/Day</th>
                    <th style={{ color: "#60a5fa", borderRight: "2px solid var(--surface3)" }}>Symbol</th>
                    <th style={{ textAlign: "center", fontWeight: 700 }}></th>
                    <th style={{ color: "#fb923c" }}>Symbol</th>
                    <th style={{ color: "#fb923c" }}>TV/Day</th>
                    <th style={{ color: "#fb923c" }}>Time Val</th>
                    <th style={{ color: "#fb923c" }}>Intrinsic</th>
                    <th style={{ color: "#fb923c" }}>LTP</th>
                    <th style={{ color: "#fb923c" }}>IV%</th>
                    <th style={{ color: "#fb923c" }}>Tag</th>
                  </tr>
                </thead>
                <tbody>
                  {data.strikes.map((s, i) => {
                    const rowBg = s.isATM ? "rgba(139, 92, 246, 0.1)" : undefined;
                    return (
                      <tr key={i} style={{ background: rowBg }}>
                        {/* CE side */}
                        <td><Tag tag={s.ce?.tag} /></td>
                        <td style={{ color: s.ce?.iv ? (s.ce.tag === "EXPENSIVE" ? "#f87171" : s.ce.tag === "CHEAP" ? "#34d399" : "#fbbf24") : "var(--edge)" }}>
                          {s.ce?.iv ? `${s.ce.iv}%` : "-"}
                        </td>
                        <td style={{ fontWeight: 600, color: "var(--text2)" }}>{s.ce?.ltp ? `${s.ce.ltp.toFixed(1)}` : "-"}</td>
                        <td style={{ color: s.ce?.intrinsic > 0 ? "#34d399" : "var(--edge)" }}>{s.ce?.intrinsic?.toFixed(1) || "-"}</td>
                        <td style={{ color: "#60a5fa" }}>{s.ce?.timeValue?.toFixed(1) || "-"}</td>
                        <td style={{ color: "var(--dim2)", fontSize: 10 }}>{s.ce?.tvPerDay?.toFixed(1) || "-"}</td>
                        <td style={{ fontSize: 9, color: "var(--edge)", borderRight: "2px solid var(--surface3)", maxWidth: 100, overflow: "hidden", textOverflow: "ellipsis" }}>{s.ce?.symbol || "-"}</td>

                        {/* Strike */}
                        <td style={{
                          textAlign: "center", fontWeight: 700, fontSize: 13,
                          color: s.isATM ? "#8b5cf6" : "var(--text2)",
                          background: s.isATM ? "rgba(139, 92, 246, 0.2)" : undefined,
                        }}>
                          {s.strike}
                          {s.isATM && <div style={{ fontSize: 8, color: "#8b5cf6" }}>ATM</div>}
                        </td>

                        {/* PE side */}
                        <td style={{ fontSize: 9, color: "var(--edge)", maxWidth: 100, overflow: "hidden", textOverflow: "ellipsis" }}>{s.pe?.symbol || "-"}</td>
                        <td style={{ color: "var(--dim2)", fontSize: 10 }}>{s.pe?.tvPerDay?.toFixed(1) || "-"}</td>
                        <td style={{ color: "#fb923c" }}>{s.pe?.timeValue?.toFixed(1) || "-"}</td>
                        <td style={{ color: s.pe?.intrinsic > 0 ? "#34d399" : "var(--edge)" }}>{s.pe?.intrinsic?.toFixed(1) || "-"}</td>
                        <td style={{ fontWeight: 600, color: "var(--text2)" }}>{s.pe?.ltp ? `${s.pe.ltp.toFixed(1)}` : "-"}</td>
                        <td style={{ color: s.pe?.iv ? (s.pe.tag === "EXPENSIVE" ? "#f87171" : s.pe.tag === "CHEAP" ? "#34d399" : "#fbbf24") : "var(--edge)" }}>
                          {s.pe?.iv ? `${s.pe.iv}%` : "-"}
                        </td>
                        <td><Tag tag={s.pe?.tag} /></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Legend */}
          <div style={{ padding: "8px 12px", display: "flex", gap: 16, fontSize: 11, color: "var(--grey-text2)", flexWrap: "wrap" }}>
            <span><Tag tag="CHEAP" /> IV/HV &lt; 0.8 — premium is underpriced</span>
            <span><Tag tag="FAIR" /> IV/HV 0.8-1.2 — reasonably priced</span>
            <span><Tag tag="EXPENSIVE" /> IV/HV &gt; 1.2 — premium is overpriced</span>
            <span style={{ color: "var(--edge)" }}>|</span>
            <span>Intrinsic = max(0, Spot-Strike) for CE, max(0, Strike-Spot) for PE</span>
            <span>Time Value = LTP - Intrinsic</span>
            <span>TV/Day = Time Value / Days to Expiry</span>
          </div>
        </>
      )}

      {!data && !loading && !error && (
        <div style={{ padding: 24, textAlign: "center", color: "var(--btn-border)" }}>
          <p>Select an index and click Refresh to analyze option premiums.</p>
          <p style={{ fontSize: 12 }}>Requires Zerodha login for live data.</p>
        </div>
      )}
    </div>
  );
}
